// View model of the Admin page's Bot subpage (`pages/AdminBotPage.vue`): plain functions over what `GET /admin/bot` returns.
import type { AdminBotProfile, AdminBotRights } from '@/api'

export type RightState = 'yes' | 'no' | 'unknown'

export interface RightEntry {
  name: string
  state: RightState
}

export interface LanguageGroup {
  /** `''` is Telegram's default for everybody without an entry of their own language. */
  language: string
  profiles: AdminBotProfile[]
}

/** The rights in the order the API sends them; `null` (Telegram did not send the field) is `unknown`, not `no`. */
export function rightsList(rights: AdminBotRights['rights']): RightEntry[] {
  return Object.entries(rights).map(([name, value]) => ({ name, state: value === null ? 'unknown' : value ? 'yes' : 'no' }))
}

/** One group per language, the default (`''`) first, the scopes of a language as the server ordered them. */
export function groupProfiles(profiles: AdminBotProfile[]): LanguageGroup[] {
  const groups = new Map<string, AdminBotProfile[]>()
  for (const profile of profiles) {
    const list = groups.get(profile.language_code) ?? []
    list.push(profile)
    groups.set(profile.language_code, list)
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)))
    .map(([language, list]) => ({ language, profiles: list }))
}

/** The languages to choose from, the default (`''`) first. */
export function languageOptions(profiles: AdminBotProfile[]): string[] {
  return groupProfiles(profiles).map((group) => group.language)
}

/** What one language has: the profile everybody sees, and the ones of single admin chats. */
export function scopesOf(profiles: AdminBotProfile[], language: string): { everybody: AdminBotProfile | null; chats: AdminBotProfile[] } {
  const mine = profiles.filter((profile) => profile.language_code === language)
  return {
    everybody: mine.find((profile) => parseScope(profile.scope).kind === 'default') ?? null,
    chats: mine.filter((profile) => parseScope(profile.scope).kind === 'chat'),
  }
}

/** `chat:123` is the command list of the admin chat 123, `default` the one everybody else sees. */
export function parseScope(scope: string): { kind: 'default' } | { kind: 'chat'; chatId: string } {
  return scope.startsWith('chat:') ? { kind: 'chat', chatId: scope.slice('chat:'.length) } : { kind: 'default' }
}

/** Telegram's dates are unix seconds. */
export function formatEpoch(value: number | null, locale?: string): string {
  if (value === null || value === undefined) return '—'
  try {
    return new Date(value * 1000).toLocaleString(locale)
  } catch {
    return String(value)
  }
}

export function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2) ?? ''
}
